import React, { useState } from 'react';
import { Link } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FiCalendar, FiUser, FiHome, FiCheck, FiX, FiClock } from 'react-icons/fi';

export default function ToursIndex({ pending_tours = [], confirmed_tours = [], completed_tours = [], cancelled_tours = [], facilities = [], user }) {
  // Check if facility_id is in URL params
  const urlParams = new URLSearchParams(window.location.search);
  const facilityIdFromUrl = urlParams.get('facility_id');
  
  const [selectedFacility, setSelectedFacility] = useState(facilityIdFromUrl || 'all');
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  
  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };
  
  // Get the selected facility object
  const selectedFacilityObject = facilities.find(f => f.id.toString() === selectedFacility);
  
  // Filter tours by selected facility
  const filterToursByFacility = (tours) => {
    if (selectedFacility === 'all') return tours;
    return tours.filter(tour => tour.facility.id.toString() === selectedFacility);
  };
  
  const filteredPendingTours = filterToursByFacility(pending_tours);
  const filteredConfirmedTours = filterToursByFacility(confirmed_tours);
  const filteredCompletedTours = filterToursByFacility(completed_tours);
  const filteredCancelledTours = filterToursByFacility(cancelled_tours);
  
  // Tour card component for reuse
  const TourCard = ({ tour, showActions = true }) => (
    <div className="p-4 border rounded-lg mb-3 hover:bg-gray-50 transition-colors">
      <div className="flex flex-col md:flex-row md:justify-between md:items-start">
        <div>
          <div className="flex items-center mb-2">
            <FiCalendar className="h-4 w-4 text-blue-600 mr-2" />
            <span className="font-medium">{formatDate(tour.scheduled_at)} at {formatTime(tour.scheduled_at)}</span>
          </div>
          
          <div className="flex items-center mb-2">
            <FiHome className="h-4 w-4 text-blue-500 mr-2" />
            <Link 
              href={`/facility_owner/facilities/${tour.facility.id}`} 
              className="text-blue-600 hover:underline"
            >
              {tour.facility.name}
            </Link>
          </div>
          
          <div className="flex items-center">
            <FiUser className="h-4 w-4 text-gray-500 mr-2" />
            <span className="text-gray-600">{tour.user ? tour.user.email : 'Anonymous'}</span>
          </div>
          
          {tour.notes && (
            <p className="mt-2 text-sm text-gray-500 border-t pt-2">{tour.notes}</p>
          )}
        </div>
        
        {showActions && (
          <div className="mt-4 md:mt-0 flex flex-col md:items-end space-y-2">
            <Badge variant={
              tour.status === 'pending' ? 'secondary' :
              tour.status === 'confirmed' ? 'success' :
              tour.status === 'completed' ? 'default' :
              'destructive'
            }>
              {tour.status.charAt(0).toUpperCase() + tour.status.slice(1)}
            </Badge>
            
            <div className="flex flex-col space-y-2 mt-2">
              <Button variant="outline" size="sm" className="w-full" asChild>
                <Link href={`/facility_owner/tours/${tour.id}`}>
                  <FiCalendar className="mr-2 h-4 w-4" />
                  View Tour
                </Link>
              </Button>
              <Button variant="ghost" size="sm" className="w-full" asChild>
                <Link href={`/facility_owner/facilities/${tour.facility.id}`}>
                  <FiHome className="mr-2 h-4 w-4" />
                  View Facility
                </Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Tours</h1>
              <p className="text-gray-500">Manage and track facility tours</p>
            </div>
          </div>
          
          {/* Facility filter */}
          <div className="mb-6">
            <Card className="mb-6">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-lg">Filter by Facility</CardTitle>
                    <CardDescription>
                      {selectedFacility === 'all' 
                        ? 'Showing tours from all facilities' 
                        : `Showing tours from ${selectedFacilityObject?.name || ''}`}
                    </CardDescription>
                  </div>
                  
                  {selectedFacility !== 'all' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedFacility('all')}
                    >
                      <FiX className="mr-2 h-4 w-4" />
                      Clear Filter
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant={selectedFacility === 'all' ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setSelectedFacility('all')}
                  >
                    <FiHome className="mr-2 h-4 w-4" />
                    All Facilities
                  </Button>
                  
                  {facilities.map(facility => (
                    <Button
                      key={facility.id}
                      variant={selectedFacility === facility.id.toString() ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedFacility(facility.id.toString())}
                    >
                      <FiHome className="mr-2 h-4 w-4" />
                      {facility.name}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          
          <Tabs defaultValue="pending" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="pending" className="flex items-center">
                <FiClock className="mr-2 h-4 w-4" />
                Pending
                {filteredPendingTours.length > 0 && (
                  <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium rounded-full px-2.5 py-0.5">
                    {filteredPendingTours.length}
                  </span>
                )}
              </TabsTrigger>
              
              <TabsTrigger value="confirmed" className="flex items-center">
                <FiCheck className="mr-2 h-4 w-4" />
                Confirmed
                {filteredConfirmedTours.length > 0 && (
                  <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium rounded-full px-2.5 py-0.5">
                    {filteredConfirmedTours.length}
                  </span>
                )}
              </TabsTrigger>
              
              <TabsTrigger value="completed" className="flex items-center">
                <FiCheck className="mr-2 h-4 w-4" />
                Completed
                {filteredCompletedTours.length > 0 && (
                  <span className="ml-2 bg-gray-100 text-gray-800 text-xs font-medium rounded-full px-2.5 py-0.5">
                    {filteredCompletedTours.length}
                  </span>
                )}
              </TabsTrigger>
              
              <TabsTrigger value="cancelled" className="flex items-center">
                <FiX className="mr-2 h-4 w-4" />
                Cancelled
                {filteredCancelledTours.length > 0 && (
                  <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium rounded-full px-2.5 py-0.5">
                    {filteredCancelledTours.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="pending">
              <Card>
                <CardHeader>
                  <CardTitle>Pending Tours</CardTitle>
                  <CardDescription>Tours that need to be confirmed</CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredPendingTours.length > 0 ? (
                    <div className="space-y-2">
                      {filteredPendingTours.map(tour => (
                        <TourCard key={tour.id} tour={tour} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No pending tours</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="confirmed">
              <Card>
                <CardHeader>
                  <CardTitle>Confirmed Tours</CardTitle>
                  <CardDescription>Upcoming tours that have been confirmed</CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredConfirmedTours.length > 0 ? (
                    <div className="space-y-2">
                      {filteredConfirmedTours.map(tour => (
                        <TourCard key={tour.id} tour={tour} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No confirmed tours</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="completed">
              <Card>
                <CardHeader>
                  <CardTitle>Completed Tours</CardTitle>
                  <CardDescription>Tours that have been completed</CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredCompletedTours.length > 0 ? (
                    <div className="space-y-2">
                      {filteredCompletedTours.map(tour => (
                        <TourCard key={tour.id} tour={tour} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No completed tours</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="cancelled">
              <Card>
                <CardHeader>
                  <CardTitle>Cancelled Tours</CardTitle>
                  <CardDescription>Tours that have been cancelled</CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredCancelledTours.length > 0 ? (
                    <div className="space-y-2">
                      {filteredCancelledTours.map(tour => (
                        <TourCard key={tour.id} tour={tour} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No cancelled tours</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </MainLayout>
  );
}
